import React from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, Ruler, Upload, ShoppingCart, Package, ArrowRight } from 'lucide-react';

const iconMap = {
  LayoutGrid: LayoutGrid,
  Ruler: Ruler,
  Upload: Upload,
  ShoppingCart: ShoppingCart,
  Package: Package
};

const ProcessSection = () => {
  const { t } = useTranslation();

  const processSteps = [
    { id: 1, icon: 'LayoutGrid', titleKey: 'process.step1Title', descKey: 'process.step1Desc' },
    { id: 2, icon: 'Ruler', titleKey: 'process.step2Title', descKey: 'process.step2Desc' },
    { id: 3, icon: 'Upload', titleKey: 'process.step3Title', descKey: 'process.step3Desc' },
    { id: 4, icon: 'ShoppingCart', titleKey: 'process.step4Title', descKey: 'process.step4Desc' },
    { id: 5, icon: 'Package', titleKey: 'process.step5Title', descKey: 'process.step5Desc' }
  ];

  return (
    <section id="process" className="py-20 bg-gradient-to-br from-cyan-50 via-blue-50 to-sky-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-cyan-100 px-4 py-2 rounded-full mb-4">
            <Package className="w-4 h-4 text-cyan-700" />
            <span className="text-sm font-semibold text-cyan-700">{t('process.badge')}</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('process.title')} <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-600">{t('process.titleHighlight')}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('process.description')}
          </p>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block relative">
          {/* Connection Line */}
          <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-cyan-300 via-blue-300 to-sky-300 opacity-30" style={{ width: '80%', margin: '0 auto' }}></div>
          
          <div className="flex justify-center items-start gap-4">
            {processSteps.map((step, index) => {
              const IconComponent = iconMap[step.icon];
              return (
                <div key={step.id} className="relative flex flex-col items-center flex-1 max-w-xs">
                  {/* Step Number Circle */}
                  <div className="relative z-10 w-48 h-48 bg-white rounded-full border-4 border-cyan-300 shadow-xl flex items-center justify-center mb-6 group hover:scale-110 hover:border-cyan-500 transition-all duration-300">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-600">
                        {step.id}
                      </div>
                    </div>
                  </div>
                  
                  {/* Step Content */}
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {t(step.titleKey)}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {t(step.descKey)}
                    </p>
                  </div>

                  {/* Arrow */}
                  {index < processSteps.length - 1 && (
                    <ArrowRight className="absolute -right-8 top-20 w-8 h-8 text-cyan-400 animate-pulse" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile/Tablet Layout */}
        <div className="lg:hidden space-y-6">
          {processSteps.map((step, index) => {
            const IconComponent = iconMap[step.icon];
            return (
              <div key={step.id} className="relative">
                <div className="flex items-start gap-4 bg-white p-6 rounded-2xl border-2 border-cyan-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div className="mt-2 text-center">
                      <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-600">
                        {step.id}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {t(step.titleKey)}
                    </h3>
                    <p className="text-gray-600">
                      {t(step.descKey)}
                    </p>
                  </div>
                </div>

                {/* Connecting Line */}
                {index < processSteps.length - 1 && (
                  <div className="flex justify-center py-2">
                    <div className="w-1 h-8 bg-gradient-to-b from-cyan-300 to-blue-300"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <button
            onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            {t('process.cta')}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
